import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3, Building2, ChevronDown, ChevronRight, Download, Loader2,
} from "lucide-react";
import api from "../../lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const STATUS = {
  completed: { label: "Completato", cls: "wl-badge wl-badge--success" },
  in_progress: { label: "In corso", cls: "wl-badge wl-badge--info" },
  not_started: { label: "Non iniziato", cls: "wl-badge wl-badge--neutral" },
};
const ROLE_LABELS = { admin: "Admin", operator: "Operatore" };

const TenantBlock = ({ courseId, tenant }) => (
  <div className="border border-border rounded-lg overflow-hidden"
       data-testid={`report-tenant-${courseId}-${tenant.org_id}`}>
    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-subtle flex-wrap">
      <div className="text-xs font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--wl-ink)" }}>
        <Building2 size={12} /> {tenant.org_name}
      </div>
      <div className="text-[11px] flex items-center gap-2 flex-wrap" style={{ color: "var(--wl-text-muted)" }}>
        <span className="wl-badge wl-badge--success" style={{ fontSize: "10px" }}>
          {tenant.n_completed}/{tenant.n_total} completati
        </span>
        {tenant.n_in_progress > 0 && (
          <span className="wl-badge wl-badge--info" style={{ fontSize: "10px" }}>{tenant.n_in_progress} in corso</span>
        )}
        {tenant.n_not_started > 0 && (
          <span className="wl-badge wl-badge--neutral" style={{ fontSize: "10px" }}>{tenant.n_not_started} non iniziati</span>
        )}
      </div>
    </div>
    {tenant.users.length === 0 ? (
      <div className="px-3 py-3 text-xs" style={{ color: "var(--wl-text-secondary)" }}>
        Nessun utente attivo in questo tenant.
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left" style={{ color: "var(--wl-text-muted)" }}>
              <th className="px-3 py-1.5 font-medium">Utente</th>
              <th className="px-3 py-1.5 font-medium hidden md:table-cell">Email</th>
              <th className="px-3 py-1.5 font-medium">Stato</th>
              <th className="px-3 py-1.5 font-medium text-center">Punteggio</th>
              <th className="px-3 py-1.5 font-medium hidden sm:table-cell">Completato il</th>
            </tr>
          </thead>
          <tbody>
            {tenant.users.map((u) => {
              const st = STATUS[u.status] || STATUS.not_started;
              return (
                <tr key={u.user_id} className="border-t border-border"
                    data-testid={`report-user-row-${courseId}-${u.user_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium" style={{ color: "var(--wl-ink)" }}>{u.name}</div>
                    <div className="text-[10px]" style={{ color: "var(--wl-text-muted)" }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </div>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell" style={{ color: "var(--wl-text-secondary)" }}>{u.email}</td>
                  <td className="px-3 py-2"><span className={st.cls} style={{ fontSize: "10px" }}>{st.label}</span></td>
                  <td className="px-3 py-2 text-center" style={{ color: "var(--wl-text-secondary)" }}>
                    {u.best_score !== null && u.best_score !== undefined ? u.best_score : "—"}
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell" style={{ color: "var(--wl-text-secondary)" }}>
                    {u.status === "completed" ? fmtDate(u.completed_at) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const FormazioneReportPanel = () => {
  const [data, setData] = useState(null);
  const [courseFilter, setCourseFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [openIds, setOpenIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/superadmin/formazione/report");
        setData(d.courses || []);
        if ((d.courses || []).length > 0) setOpenIds(new Set([d.courses[0].id]));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore nel caricamento del report");
        setData([]);
      }
    })();
  }, []);

  const orgs = useMemo(() => {
    const m = new Map();
    (data || []).forEach((c) => c.tenants.forEach((t) => m.set(t.org_id, t.org_name)));
    return [...m.entries()];
  }, [data]);

  const visible = useMemo(() => (data || [])
    .filter((c) => !courseFilter || c.id === courseFilter)
    .map((c) => ({ ...c, tenants: c.tenants.filter((t) => !orgFilter || t.org_id === orgFilter) }))
    .filter((c) => !orgFilter || c.tenants.length > 0), [data, courseFilter, orgFilter]);

  const toggle = (id) => setOpenIds((s) => {
    const c = new Set(s);
    if (c.has(id)) c.delete(id); else c.add(id);
    return c;
  });

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (courseFilter) params.set("course_id", courseFilter);
      if (orgFilter) params.set("org_id", orgFilter);
      const qs = params.toString();
      const res = await api.get(`/superadmin/formazione/report/export${qs ? `?${qs}` : ""}`,
        { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : "Report_Formazione.xlsx";
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report Excel scaricato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile generare l'export.");
    } finally {
      setExporting(false);
    }
  };

  if (data === null) return null;

  return (
    <div className="mt-10" data-testid="formazione-report-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-sm font-semibold inline-flex items-center gap-2" style={{ color: "var(--wl-ink)" }}>
          <BarChart3 size={15} /> Report completamenti
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(data.length > 1) && (
            <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} className="wl-input"
                    style={{ width: "auto", height: "34px", fontSize: "12px", maxWidth: "220px" }}
                    data-testid="report-course-filter">
              <option value="">Tutti i corsi</option>
              {data.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          )}
          {orgs.length > 1 && (
            <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} className="wl-input"
                    style={{ width: "auto", height: "34px", fontSize: "12px", maxWidth: "200px" }}
                    data-testid="report-org-filter">
              <option value="">Tutti i tenant</option>
              {orgs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          <button type="button" onClick={exportExcel} disabled={exporting || data.length === 0}
                  className="wl-btn wl-btn--primary wl-btn--sm" data-testid="report-export-btn">
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Esporta Excel
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-6 text-center text-xs"
             style={{ color: "var(--wl-text-secondary)" }} data-testid="report-empty">
          Nessun dato: carica un corso e assegnalo a un tenant per vedere i completamenti.
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((c) => {
            const isOpen = openIds.has(c.id);
            const totals = c.tenants.reduce((acc, t) => ({
              done: acc.done + t.n_completed, all: acc.all + t.n_total,
            }), { done: 0, all: 0 });
            return (
              <div key={c.id} className="bg-surface border border-border rounded-xl overflow-hidden"
                   data-testid={`report-course-${c.id}`}>
                <button type="button" onClick={() => toggle(c.id)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-subtle transition-colors"
                        data-testid={`report-course-toggle-${c.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown size={14} className="shrink-0" style={{ color: "var(--wl-text-muted)" }} />
                            : <ChevronRight size={14} className="shrink-0" style={{ color: "var(--wl-text-muted)" }} />}
                    <span className="text-sm font-semibold truncate" style={{ color: "var(--wl-ink)" }}>{c.title}</span>
                    {c.category && (
                      <span className="wl-badge wl-badge--neutral shrink-0" style={{ fontSize: "10px" }}>{c.category}</span>
                    )}
                  </div>
                  <div className="text-[11px] shrink-0 flex items-center gap-2" style={{ color: "var(--wl-text-muted)" }}>
                    {c.tenants.length === 0
                      ? <span className="wl-badge wl-badge--warning" style={{ fontSize: "10px" }}>Nessun tenant assegnato</span>
                      : <span>{totals.done}/{totals.all} completati · {c.tenants.length} tenant</span>}
                  </div>
                </button>
                {isOpen && c.tenants.length > 0 && (
                  <div className="px-4 pb-4 space-y-3">
                    {c.tenants.map((t) => <TenantBlock key={t.org_id} courseId={c.id} tenant={t} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
